
import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Sidebar from './components/Sidebar';
import ClientManagement from './components/ClientManagement';
import UnionManagement from './components/UnionManagement';
import UnionDetail from './components/UnionDetail';
import Login from './components/Login';
import { User, UserRole } from './types';
import { LogOut, Wifi, AlertTriangle } from 'lucide-react';
import { auth, isConfigured } from './lib/firebase';
import { api } from './services/api';
import { onAuthStateChanged, signOut } from "firebase/auth";
import type { User as FirebaseUser } from "firebase/auth";

interface LayoutProps {
  children: React.ReactNode;
  user: User;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onLogout }) => {
  return (
    <div className="flex h-screen bg-gray-100" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="flex justify-between items-center bg-white shadow-sm h-16 px-6 z-10 flex-shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-800">入金消込システム</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button 
                onClick={onLogout}
                className="text-gray-500 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-gray-100"
                title="ログアウト"
            >
                <LogOut size={20} />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isConfigured) {
        setIsLoading(false);
        return;
    }

    if (!auth) {
        setInitError("Firebase Authentication service could not be initialized.");
        setIsLoading(false);
        return;
    }

    const timeoutId = setTimeout(() => {
        if (isLoading) {
            console.warn("Auth check timed out.");
            setIsLoading(false);
        }
    }, 7000);

    try {
        const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
            clearTimeout(timeoutId);
            if (user) {
                setCurrentUser({
                    id: user.uid,
                    email: user.email || '',
                    name: user.email?.split('@')[0] || 'User',
                    role: UserRole.USER
                });
                setIsAuthenticated(true);
                try {
                    await api.data.initializeDemoData();
                } catch (e) {
                    console.warn("Init data check skipped:", e);
                }
            } else {
                setCurrentUser(null);
                setIsAuthenticated(false);
            }
            setIsLoading(false);
        }, (error: any) => {
            console.error("Auth Listener Error:", error);
            setIsAuthenticated(false);
            setIsLoading(false);
        });

        return () => {
            clearTimeout(timeoutId);
            unsubscribe();
        };
    } catch (e: any) {
        console.error("Auth Setup Error:", e);
        setInitError(typeof e === 'string' ? e : e?.message || "Setup Error");
        setIsLoading(false);
    }
  }, []);

  const handleLogin = (user: User) => {};

  const handleLogout = async () => {
    try {
        if (auth) await signOut(auth);
        setIsAuthenticated(false);
    } catch(e) {
        console.error(e);
    }
  };

  if (initError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
            <div className="bg-white p-8 rounded shadow-lg max-w-md w-full text-center">
                <AlertTriangle size={48} className="text-red-500 mx-auto mb-4"/>
                <h2 className="text-xl font-bold text-gray-800 mb-2">システムエラー</h2>
                <div className="bg-red-50 text-red-700 p-3 rounded text-left text-xs font-mono mb-6 overflow-x-auto">
                    {String(initError)}
                </div>
                <button onClick={() => window.location.reload()} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">再読み込み</button>
            </div>
        </div>
      );
  }

  if (isLoading) {
    return (
        <div className="h-screen w-screen flex flex-col items-center justify-center bg-gray-50 text-gray-500 gap-4" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
            <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <p className="flex items-center gap-2"><Wifi size={18}/> サーバーへ接続中...</p>
        </div>
    );
  }

  if (!isAuthenticated || !currentUser) {
    return (
      <div style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <HashRouter>
      <Layout user={currentUser} onLogout={handleLogout}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/clients" element={<ClientManagement />} />
          <Route path="/union" element={<UnionManagement />} />
          <Route path="/union/:id" element={<UnionDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </HashRouter>
  );
};

export default App;
